class HeaderBiosolutionsMenu extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.panel = this.querySelector('.biosolutions-menu__panel');
    this.backdrop = this.querySelector('.biosolutions-menu__backdrop');
    this.tabs = [...this.querySelectorAll('[data-biosolutions-tab]')];
    this.panels = [...this.querySelectorAll('[data-biosolutions-panel]')];
    this.closeButtons = [...this.querySelectorAll('[data-biosolutions-close]')];
    this.headerSection = this.closest('.section-header');
    this.onKeydown = this.onKeydown.bind(this);
    this.updatePosition = this.updatePosition.bind(this);
  }

  connectedCallback() {
    if (!this.details || !this.summary || !this.panel || !this.backdrop) return;

    this.tabs.forEach((tab) => {
      const activate = () => this.activateTab(Number(tab.dataset.biosolutionsTab));
      tab.addEventListener('mouseenter', activate);
      tab.addEventListener('focus', activate);
      tab.addEventListener('click', activate);
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event, tab));
    });

    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));

    // Render overlays at body level so a transformed sticky header cannot
    // create a zoom-dependent positioning context for the panel.
    document.body.append(this.backdrop, this.panel);

    this.details.addEventListener('toggle', () => {
      if (this.details.open) {
        document.querySelectorAll('header-biosolutions-menu details[open]').forEach((menu) => {
          if (menu !== this.details) menu.removeAttribute('open');
        });
        this.panel.classList.add('is-open');
        this.backdrop.classList.add('is-open');
        this.updatePosition();
        requestAnimationFrame(this.updatePosition);
        document.addEventListener('keydown', this.onKeydown);
      } else {
        this.panel.classList.remove('is-open');
        this.backdrop.classList.remove('is-open');
        document.removeEventListener('keydown', this.onKeydown);
      }
    });

    window.addEventListener('resize', this.updatePosition);
    window.addEventListener('scroll', this.updatePosition, { passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.updatePosition);
    window.removeEventListener('scroll', this.updatePosition);
    this.panel?.remove();
    this.backdrop?.remove();
  }

  activateTab(index) {
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
  }

  onTabKeydown(event, currentTab) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const currentIndex = this.tabs.indexOf(currentTab);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;

    this.activateTab(nextIndex);
    this.tabs[nextIndex].focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close(true);
  }

  updatePosition() {
    if (!this.details.open || !this.panel || !this.backdrop) return;

    const headerRect = this.headerSection?.getBoundingClientRect();
    if (!headerRect) return;

    // A one-pixel vertical overlap covers the header separator without using
    // horizontal offsets or transforms that vary with browser zoom.
    this.panel.style.left = `${headerRect.left}px`;
    this.panel.style.top = `${headerRect.bottom - 1}px`;
    this.panel.style.width = `${headerRect.width}px`;
    this.panel.style.setProperty('--biosolutions-menu-viewport-top', `${headerRect.bottom}px`);

    this.backdrop.style.left = '0';
    this.backdrop.style.top = `${headerRect.bottom}px`;
    this.backdrop.style.width = '100vw';
  }

  close(returnFocus = false) {
    this.details.removeAttribute('open');
    if (returnFocus) this.summary.focus();
  }
}

if (!customElements.get('header-biosolutions-menu')) {
  customElements.define('header-biosolutions-menu', HeaderBiosolutionsMenu);
}
